import React, { useEffect, useState } from "react";
import {
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaMapMarkerAlt,
  FaPlus,
  FaRegCalendarAlt,
} from "react-icons/fa";
import { api } from "./session";
import { eventEmoji, useEventSaves } from "./eventsApi";
import { useFavorites } from "./FavoritesContext";
import EventDetailsSheet from "./EventDetailsSheet";
import "./HubEventsRail.css";

/* =========================================================
   HubEventsRail   "Upcoming events" on the hub.
   Cards: flyer (or a themed tile), a purple MON/DD badge, the
   title, "Sat, May 24 · 5:00 PM", and "📍 venue · San Antonio".
   Tapping a card opens the full EVENT SHEET here on the hub
   (card-first)   hearts inside use the same account save + sign-in
   gate as community pages. The gold card at the end posts an event
   via the gohub:post-event CustomEvent (HubQuickActions opens the
   PostEventSheet, sign-in gated).
   Renders nothing until at least one upcoming event exists.

   The section header uses the shared .hub-sechead styles from
   Hub.css. Place on the hub with:  <HubEventsRail />
   ========================================================= */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/* "2026-05-24" → { mon: "MAY", day: "24", weekday: "Sat", label: "May 24" } */
function dateParts(value) {
  const [year, month, day] = String(value || "").split("-").map(Number);

  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);

  return {
    mon: MONTHS[month - 1].toUpperCase(),
    day: String(day),
    weekday: WEEKDAYS[date.getDay()],
    label: `${MONTHS[month - 1]} ${day}`,
  };
}

/* "17:30" → "5:30 PM" */
function formatTime(value) {
  if (!value) return "";

  const [hour, minute] = String(value).split(":").map(Number);

  if (!Number.isFinite(hour)) return "";

  const suffix = hour >= 12 ? "PM" : "AM";

  return `${hour % 12 || 12}:${String(minute || 0).padStart(2, "0")} ${suffix}`;
}

export default function HubEventsRail() {
  const { user, requestSignInPrompt, showToast } = useFavorites();
  const { isEventSaved, toggleEventSave } = useEventSaves(user);
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const railRef = React.useRef(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });

  useEffect(() => {
    const openFromHubSearch = (event) => {
      const selected = event?.detail?.event;
      if (selected && typeof selected === "object") setActiveEvent(selected);
    };

    window.addEventListener("gohub:open-event", openFromHubSearch);
    return () => window.removeEventListener("gohub:open-event", openFromHubSearch);
  }, []);

  const updateArrows = () => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth - 2;
    setCanScroll({ left: el.scrollLeft > 2, right: el.scrollLeft < max });
  };

  useEffect(() => {
    const raf = window.requestAnimationFrame(updateArrows);
    window.addEventListener("resize", updateArrows);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", updateArrows);
    };
  }, [events, showAll]);

  const page = (dir) => {
    const el = railRef.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollBy({
      left: dir * el.clientWidth * 0.85,
      behavior: reduce ? "auto" : "smooth",
    });
  };

  useEffect(() => {
    let alive = true;
    api
      .get("/api/events/upcoming?limit=100")
      .then((response) => {
        if (alive) setEvents(response?.data?.events || []);
      })
      .catch(() => {
        /* hub stays silent about events on failure */
      })
      .finally(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!loaded || events.length === 0) return null;

  const handleToggleEventSave = async (ev) => {
    if (!user) {
      requestSignInPrompt({ kind: "event-save", name: ev.title });
      return;
    }
    try {
      const saved = await toggleEventSave(ev.id);
      showToast(saved ? `Saved ${ev.title} ♥` : "Removed from saved");
    } catch {
      showToast("Couldn't update saved events");
    }
  };

  const handleToggleAll = () => {
    const next = !showAll;
    setShowAll(next);

    if (!next) {
      window.requestAnimationFrame(() => {
        railRef.current?.scrollTo({ left: 0, behavior: "auto" });
        updateArrows();
      });
    }
  };

  return (
    <section className={`her ${showAll ? "her--all" : ""}`} aria-labelledby="her-title">
      <div className="hub-sechead">
        <span className="hub-sechead__ic hub-sechead__ic--red" aria-hidden="true">
          <FaRegCalendarAlt />
        </span>

        <div className="hub-sechead__tx">
          <h2 id="her-title">
            Upcoming events
            <span className="hub-sechead__count">{events.length}</span>
          </h2>
          <p>Happening in and around San Antonio.</p>
        </div>

        <div className="her-head-actions">
          <button
            type="button"
            className={`her-viewall ${showAll ? "is-open" : ""}`}
            aria-expanded={showAll}
            aria-controls="her-event-list"
            onClick={handleToggleAll}
          >
            {showAll ? "Show less" : "View all"}
            <FaArrowRight aria-hidden="true" />
          </button>

          {!showAll && (
            <span className="her-nav">
              <button
                type="button"
                aria-label="Scroll events left"
                disabled={!canScroll.left}
                onClick={() => page(-1)}
              >
                <FaChevronLeft />
              </button>
              <button
                type="button"
                aria-label="Scroll events right"
                disabled={!canScroll.right}
                onClick={() => page(1)}
              >
                <FaChevronRight />
              </button>
            </span>
          )}
        </div>
      </div>

      <div
        id="her-event-list"
        className="her-rail"
        ref={railRef}
        onScroll={showAll ? undefined : updateArrows}
      >
        {events.map((ev, index) => {
          const when = dateParts(ev.event_date);
          const time = formatTime(ev.start_time);
          const whenLine = [
            when ? `${when.weekday}, ${when.label}` : "",
            time,
          ]
            .filter(Boolean)
            .join(" · ");
          const whereLine = [
            ev.venue_name || ev.address || (ev.community_slug ? `${ev.community_slug} community` : ""),
            ev.city || "San Antonio",
          ]
            .filter(Boolean)
            .join(" · ");

          return (
            <button
              key={ev.id}
              type="button"
              className="her-card"
              onClick={() => setActiveEvent(ev)}
            >
              <span
                className={`her-fly ${ev.image_urls?.[0] ? "" : `her-ph-${index % 4}`}`}
                aria-hidden="true"
              >
                {ev.image_urls?.[0] ? (
                  <img src={ev.image_urls[0]} alt="" loading="lazy" />
                ) : (
                  <span className="her-fly__ic">{ev.icon || eventEmoji(ev)}</span>
                )}

                {when && (
                  <span className="her-date">
                    <small>{when.mon}</small>
                    <b>{when.day}</b>
                  </span>
                )}
              </span>

              <span className="her-bd">
                <span className="her-nm">{ev.title}</span>
                {whenLine && <span className="her-when">{whenLine}</span>}
                {whereLine && (
                  <span className="her-where">
                    <FaMapMarkerAlt aria-hidden="true" />
                    {whereLine}
                  </span>
                )}
              </span>
            </button>
          );
        })}

        <button
          type="button"
          className="her-card her-card--cta"
          onClick={() => window.dispatchEvent(new CustomEvent("gohub:post-event"))}
        >
          <span className="her-cta__ic" aria-hidden="true">
            <FaPlus />
          </span>
          <span className="her-cta__t">
            Post
            <br />
            an event
          </span>
          <FaArrowRight aria-hidden="true" />
        </button>
      </div>

      {activeEvent && (
        <EventDetailsSheet
          event={activeEvent}
          onClose={() => setActiveEvent(null)}
          isEventSaved={isEventSaved}
          onToggleSave={handleToggleEventSave}
        />
      )}
    </section>
  );
}
